import { createFileRoute, useRouter } from "@tanstack/react-router";
import { LocaleLink } from "@/components/LocaleLink";
import { MatchQuiz } from "@/components/murano/MatchQuiz";
import { MatchResults } from "@/components/murano/MatchResults";
import {
  fetchCandidates,
  parseAnswersFromSearch,
  hasAnyAnswer,
  type MuranoType,
  type Room,
  type ColorSlug,
  type SizeSlug,
  type PriceBand,
  type WallLook,
  type DealBreaker,
} from "@/lib/muranoMatch";
import { SITE_URL } from "@/lib/seo";

type Search = {
  type?: MuranoType;
  room?: Room;
  color?: ColorSlug;
  size?: SizeSlug;
  price?: PriceBand;
  wall?: WallLook;
  deal?: DealBreaker;
};

export const Route = createFileRoute("/en/murano/match")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const pick = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined => {
      return typeof v === "string" && (allowed as readonly string[]).includes(v)
        ? (v as T)
        : undefined;
    };
    return {
      type: pick(raw.type, ["loft", "mushroom", "wall", "any"] as const),
      room: pick(raw.room, ["dining", "living", "bedroom", "bath", "any"] as const),
      color: pick(raw.color, [
        "lyserod", "gul", "gron", "blaa", "rod", "orange", "lilla", "hvid", "any",
      ] as const),
      size: pick(raw.size, ["kompakt", "klassisk", "stor", "imponerende", "any"] as const),
      price: pick(raw.price, ["under2500", "mid", "high", "premium", "any"] as const),
      wall: pick(raw.wall, ["cream", "pastel", "earth", "moody", "raw", "clash", "any"] as const),
      deal: pick(raw.deal, ["today", "monday", "patient", "any"] as const),
    };
  },
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ deps }) => {
    const answers = parseAnswersFromSearch(deps.search as Record<string, string | undefined>);
    // Only show results when at least one real answer is present in the URL.
    // validateSearch always returns the full Search shape (with undefined values),
    // so we can't rely on Object.keys(deps.search).length here — that would always
    // be > 0 and would skip the quiz entirely.
    const hasRealAnswer = Object.values(deps.search).some(
      (v) => typeof v === "string" && v.length > 0 && v !== "any",
    );
    if (!hasRealAnswer && !hasAnyAnswer(answers)) return { mode: "quiz" as const };
    const result = await fetchCandidates(answers);
    return { mode: "results" as const, result, answers };
  },
  head: () => ({
    meta: [
      { title: "Find your Murano — Farveladeland" },
      {
        name: "description",
        content:
          "Five quick questions and we'll find the Murano lamp that fits you — type, colour, size and budget.",
      },
      { property: "og:title", content: "Find your Murano — Farveladeland" },
      {
        property: "og:description",
        content: "A short guide to the right hand-blown Murano lamp.",
      },
      { rel: "canonical", href: `${SITE_URL}/en/murano/match` } as never,
    ],
  }),
  component: MatchPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
        <p className="eyebrow">Ups</p>
        <h1 className="mt-4 font-display text-5xl">Noget gik galt</h1>
        <p className="mt-4 text-muted-foreground">{error.message}</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background"
        >
          Prøv igen
        </button>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
      <p className="eyebrow">404</p>
      <h1 className="mt-4 font-display text-5xl">Siden findes ikke</h1>
      <LocaleLink
        to="/murano"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background"
      >
        Til Murano →
      </LocaleLink>
    </div>
  ),
});

function MatchPage() {
  const data = Route.useLoaderData();
  if (data.mode === "quiz") {
    return <MatchQuiz />;
  }
  return <MatchResults result={data.result} answers={data.answers} resetHref="/murano/match" />;
}
